import React from 'react';
import Section from './section';
import { Col, Row } from 'antd';
import BarChart from './barChart';
import SimpleReportTable, { SimpleReportTableColumn } from './simpleReportTable';
import styles from '../index.module.scss';

interface iDriverRiskAnalysisProps {
    cabRiskData?: any[];
    dmsDetailedData?: any;
    cabRiskTable?: any[];
    analysisDescription?: string;
}

// 驾驶员风险分析与危险车辆统计模块
const DriverRiskAnalysis = (props: iDriverRiskAnalysisProps) => {
    const { cabRiskData = [], dmsDetailedData = {}, cabRiskTable = [], analysisDescription = '' } = props;
    // 处理柱状图数据 - 驾驶员风险统计
    const processChartData = () => {
        if (!cabRiskData || !Array.isArray(cabRiskData) || cabRiskData.length === 0) {
            return [];
        }

        return [{
            name: '驾驶员风险统计',
            value: cabRiskData.map(item => [
                item.name || '风险类型',
                Number((item.value || 0).toFixed(2))
            ] as [string, number])
        }];
    };

    // 处理表格数据
    const processTableData = () => {
        if (dmsDetailedData?.vehicle_dms_data && Array.isArray(dmsDetailedData.vehicle_dms_data)) {
            return dmsDetailedData.vehicle_dms_data.map((item: any) => ({
                plateNumber: item.plate_number || '--',
                mentalState: '正常', // 可以根据数据判断
                fatigueDrivingPer100km: item.tired_100ct || 0,
                phoneCallPer100km: item.call_100ct || 0,
                smokingPer100km: item.smoke_100ct || 0,
                distractedDrivingPer100km: item.distracted_100ct || 0,
            }));
        }
        return [];
    };

    const chartData = processChartData();
    const tableData = processTableData();

    // 生成分析结论
    const generateAnalysis = () => {
        if (!dmsDetailedData?.vehicle_dms_data || dmsDetailedData.vehicle_dms_data.length === 0) {
            return '暂无驾驶员风险分析数据。建议加强驾驶员行为监测，提高行车安全。';
        }

        const vehicles = dmsDetailedData.vehicle_dms_data;
        const totalVehicles = vehicles.length;

        // 统计各类风险
        const riskVehicles = vehicles.filter((v: any) =>
            (v.tired_100ct || 0) > 0 ||
            (v.call_100ct || 0) > 0 ||
            (v.smoke_100ct || 0) > 0 ||
            (v.distracted_100ct || 0) > 0
        );

        let analysis = `本期统计了${totalVehicles}辆车辆的驾驶员行为数据。`;

        if (riskVehicles.length === 0) {
            analysis += '所有车辆驾驶员行为表现良好，未发现明显的风险行为。建议继续保持良好的驾驶习惯。';
        } else {
            analysis += `其中${riskVehicles.length}辆车辆存在不同程度的风险行为，需要重点关注和改进。`;

            // 分析具体风险类型
            const fatigueCount = vehicles.filter((v: any) => (v.tired_100ct || 0) > 0).length;
            const phoneCount = vehicles.filter((v: any) => (v.call_100ct || 0) > 0).length;
            const smokeCount = vehicles.filter((v: any) => (v.smoke_100ct || 0) > 0).length;
            const distractedCount = vehicles.filter((v: any) => (v.distracted_100ct || 0) > 0).length;

            if (fatigueCount > 0) analysis += `\n疲劳驾驶风险：${fatigueCount}辆车辆存在疲劳驾驶行为。`;
            if (phoneCount > 0) analysis += `\n拨打电话风险：${phoneCount}辆车辆存在行驶中拨打电话行为。`;
            if (smokeCount > 0) analysis += `\n抽烟风险：${smokeCount}辆车辆存在行驶中抽烟行为。`;
            if (distractedCount > 0) analysis += `\n分神驾驶风险：${distractedCount}辆车辆存在分神驾驶行为。`;
        }

        return analysis;
    };

    const defaultAnalysis = generateAnalysis();

    // 表格列定义
    const columns: SimpleReportTableColumn[] = [
        {
            title: '车牌号',
            dataIndex: 'plateNumber',
            key: 'plateNumber',
            width: 150,
        },
        {
            title: '精神状态',
            dataIndex: 'mentalState',
            key: 'mentalState',
            width: 120,
        },
        {
            title: '疲劳驾驶百公里次数',
            dataIndex: 'fatigueDrivingPer100km',
            key: 'fatigueDrivingPer100km',
            width: 180,
            render: (value: number) => value !== undefined && value !== null ? value.toFixed(2) : '--',
        },
        {
            title: '拨打电话百公里次数',
            dataIndex: 'phoneCallPer100km',
            key: 'phoneCallPer100km',
            width: 180,
            render: (value: number) => value !== undefined && value !== null ? value.toFixed(2) : '--',
        },
        {
            title: '抽烟百公里次数',
            dataIndex: 'smokingPer100km',
            key: 'smokingPer100km',
            width: 160,
            render: (value: number) => value !== undefined && value !== null ? value.toFixed(2) : '--',
        },
        {
            title: '分神驾驶百公里次数',
            dataIndex: 'distractedDrivingPer100km',
            key: 'distractedDrivingPer100km',
            width: 180,
            render: (value: number) => value !== undefined && value !== null ? value.toFixed(2) : '--',
        },
    ];

    return (
        <Section title="十一、驾驶员风险分析与危险车辆统计">
            <>
                <Row gutter={24} style={{ minHeight: '420px' }}>
                    {/* 左侧柱状图 */}
                    <Col span={12}>
                        <div style={{
                            padding: '20px',
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            height: '100%',
                            border: '1px solid #f0f0f0',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                        }}>
                            {chartData.length > 0 ? (
                                <BarChart
                                    data={chartData}
                                    title="驾驶员风险统计"
                                    xAxisLabel="风险类型"
                                    yAxisLabel="次/百公里"
                                    style={{ width: '100%', height: '360px' }}
                                    color={['#1890ff', '#52c41a', '#faad14', '#ff4d4f', '#722ed1']}
                                />
                            ) : (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '360px',
                                    color: '#999'
                                }}>
                                    暂无驾驶员风险数据
                                </div>
                            )}
                        </div>
                    </Col>

                    {/* 右侧数据表格 */}
                    <Col span={12}>
                        <div style={{
                            padding: '20px',
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            height: '100%',
                            border: '1px solid #f0f0f0',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                        }}>
                            <SimpleReportTable
                                columns={columns}
                                dataSource={tableData}
                                rowKey="plateNumber"
                                maxHeight={300}
                                scrollX
                                emptyText="暂无驾驶员风险数据"
                            />
                        </div>
                    </Col>
                </Row>

                {(analysisDescription || defaultAnalysis) && (
                    <div className={styles.analysisBlock}>
                        <h4>分析结果</h4>
                        <div className={styles.analysisContent}>
                            {analysisDescription || defaultAnalysis}
                        </div>
                    </div>
                )}
            </>
        </Section>
    );
};

export default DriverRiskAnalysis;
